'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  id: number;
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface CompanyTimelineProps {
  className?: string;
}

const CompanyTimeline = ({ className = '' }: CompanyTimelineProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const milestones: Milestone[] = [
    {
      id: 1,
      year: '2010',
      title: 'Company Founded',
      description: 'AIVA Medical Supply established with a vision to revolutionize medical equipment distribution through precision and reliability.',
      icon: 'RocketLaunchIcon',
    },
    {
      id: 2,
      year: '2012',
      title: 'FDA Registration',
      description: 'Achieved FDA registration and established comprehensive quality management systems for medical device distribution.',
      icon: 'ShieldCheckIcon',
    },
    {
      id: 3,
      year: '2015',
      title: 'International Expansion',
      description: 'Expanded operations to serve healthcare facilities across North America, Europe, and Asia Pacific regions.',
      icon: 'GlobeAltIcon',
    },
    {
      id: 4,
      year: '2017',
      title: 'ISO Certification',
      description: 'Obtained ISO 13485:2016 and ISO 9001:2015 certifications, demonstrating commitment to quality management excellence.',
      icon: 'DocumentCheckIcon',
    },
    {
      id: 5,
      year: '2019',
      title: 'Advanced Warehouse',
      description: 'Opened state-of-the-art 150,000 sq ft climate-controlled warehouse facility with advanced inventory management systems.',
      icon: 'BuildingOfficeIcon',
    },
    {
      id: 6,
      year: '2021',
      title: 'Technology Integration',
      description: 'Implemented real-time tracking and AI-powered inventory optimization for enhanced supply chain efficiency.',
      icon: 'CpuChipIcon',
    },
    {
      id: 7,
      year: '2023',
      title: 'Sustainability Initiative',
      description: 'Launched comprehensive sustainability program focusing on eco-friendly packaging and carbon-neutral shipping options.',
      icon: 'SparklesIcon',
    },
    {
      id: 8,
      year: '2025',
      title: 'Industry Leadership',
      description: 'Serving 5,000+ healthcare facilities across 50+ countries with 24/7 technical support and expert consultation services.',
      icon: 'TrophyIcon',
    },
  ];

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-white ${className}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="h-96 bg-surface rounded-2xl animate-pulse"></div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            Our Journey
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Company Milestones & Innovation
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From our founding in 2010 to becoming a trusted global partner for healthcare institutions, our journey reflects continuous innovation and unwavering commitment to excellence.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-primary via-accent to-brand-accent hidden lg:block"></div>

          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.id}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                  <div className="bg-surface rounded-2xl p-6 shadow-brand hover:shadow-brand-lg transition-all duration-300 border border-border">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name={milestone.icon as any} size={24} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl font-bold text-primary">{milestone.year}</span>
                          <div className="h-px flex-1 bg-border"></div>
                        </div>
                        <h3 className="text-xl font-bold text-text-primary mb-2">{milestone.title}</h3>
                        <p className="text-sm text-text-secondary leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white border-4 border-primary rounded-full items-center justify-center shadow-brand z-10">
                  <div className="w-8 h-8 bg-primary rounded-full"></div>
                </div>

                <div className="hidden lg:block w-5/12"></div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-primary via-accent to-brand-accent rounded-2xl p-8 lg:p-12 text-white text-center">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Building the Future of Medical Supply
          </h3>
          <p className="text-lg opacity-90 max-w-3xl mx-auto mb-8">
            Our commitment to innovation continues as we invest in advanced technologies, expand our global network, and develop new solutions that empower healthcare providers to deliver exceptional patient care.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <div>
              <p className="text-4xl font-bold mb-1">15+</p>
              <p className="text-sm opacity-80">Years of Excellence</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-1">5,000+</p>
              <p className="text-sm opacity-80">Healthcare Partners</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-1">50+</p>
              <p className="text-sm opacity-80">Countries Worldwide</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;